import { useCallback, useMemo, useState } from 'react';
import {
  getDefaultTransform,
  normalizeTitleComponents,
  prepareTitleComponentsForSave,
  validateTitleComponents,
} from './titleTransformUtils';
import {
  DOCUMENT_TYPE_FIELD_DEFAULT,
  DOCUMENT_TYPE_FIELDS,
  EMAIL_JOIN_DEFAULT,
  EMAIL_JOIN_TYPES,
} from './configConstants';
import { daysToDuration, parseDurationToDays } from './configDuration';

const useConfigDocument = () => {
  const [retentionDays, setRetentionDays] = useState(7);
  const [emailJoinType, setEmailJoinType] = useState(EMAIL_JOIN_DEFAULT);
  const [shouldIncludeReceivedAt, setShouldIncludeReceivedAt] = useState(false);
  const [shouldSendEmailSuccessAck, setShouldSendEmailSuccessAck] = useState(false);
  const [defaultDocumentTypeField, setDefaultDocumentTypeField] = useState(DOCUMENT_TYPE_FIELD_DEFAULT);
  const [titleComponents, setTitleComponents] = useState([]);
  const [editingTitleComponent, setEditingTitleComponent] = useState(null);

  const titleComponentValidationErrors = useMemo(
    () => validateTitleComponents(titleComponents),
    [titleComponents]
  );
  const hasTitleComponentErrors = titleComponentValidationErrors.length > 0;

  const applyFetched = useCallback((configData) => {
    const retentionDaysValue = parseDurationToDays(configData?.documentConfig?.maxFiledAwayAge);
    setRetentionDays(retentionDaysValue);

    const joinTypeRaw = configData?.documentConfig?.emailJoinType;
    const joinType = EMAIL_JOIN_TYPES.includes(joinTypeRaw) ? joinTypeRaw : EMAIL_JOIN_DEFAULT;
    setEmailJoinType(joinType);

    const documentTypeFieldRaw = configData?.documentConfig?.defaultDocumentTypeField;
    const documentTypeField = DOCUMENT_TYPE_FIELDS.includes(documentTypeFieldRaw)
      ? documentTypeFieldRaw
      : DOCUMENT_TYPE_FIELD_DEFAULT;
    setDefaultDocumentTypeField(documentTypeField);

    const shouldIncludeReceivedAtValue = configData?.documentConfig?.shouldIncludeReceivedAt === true;
    setShouldIncludeReceivedAt(shouldIncludeReceivedAtValue);

    const shouldSendEmailSuccessAckValue = configData?.documentConfig?.shouldSendEmailSuccessAck === true;
    setShouldSendEmailSuccessAck(shouldSendEmailSuccessAckValue);

    const titleComps = normalizeTitleComponents(configData?.documentConfig?.titleComponents || []);
    setTitleComponents(titleComps);
    setEditingTitleComponent(null);

    return {
      retentionDays: retentionDaysValue,
      emailJoinType: joinType,
      shouldIncludeReceivedAt: shouldIncludeReceivedAtValue,
      shouldSendEmailSuccessAck: shouldSendEmailSuccessAckValue,
      defaultDocumentTypeField: documentTypeField,
      titleComponents: JSON.parse(JSON.stringify(titleComps)),
    };
  }, []);

  const snapshot = useCallback(() => ({
    retentionDays,
    emailJoinType,
    shouldIncludeReceivedAt,
    shouldSendEmailSuccessAck,
    defaultDocumentTypeField,
    titleComponents: JSON.parse(JSON.stringify(titleComponents)),
  }), [
    retentionDays,
    emailJoinType,
    shouldIncludeReceivedAt,
    shouldSendEmailSuccessAck,
    defaultDocumentTypeField,
    titleComponents,
  ]);

  const restore = useCallback((previous) => {
    setRetentionDays(previous.retentionDays);
    setEmailJoinType(previous.emailJoinType ?? EMAIL_JOIN_DEFAULT);
    setShouldIncludeReceivedAt(previous.shouldIncludeReceivedAt ?? false);
    setShouldSendEmailSuccessAck(previous.shouldSendEmailSuccessAck ?? false);
    setDefaultDocumentTypeField(previous.defaultDocumentTypeField ?? DOCUMENT_TYPE_FIELD_DEFAULT);
    setTitleComponents(JSON.parse(JSON.stringify(previous.titleComponents)));
    setEditingTitleComponent(null);
  }, []);

  const handleAddToken = (tokenKey) => {
    const newComponent = {
      type: 'TOKEN',
      token: tokenKey,
      value: null,
      transform: getDefaultTransform(tokenKey),
    };
    setTitleComponents([...titleComponents, newComponent]);
  };

  const handleAddLiteral = () => {
    const newComponent = { type: 'LITERAL', token: null, value: ' - ', transform: null };
    setTitleComponents([...titleComponents, newComponent]);
  };

  const handleMoveComponent = (index, direction) => {
    const newComponents = [...titleComponents];
    const targetIndex = index + direction;

    if (targetIndex < 0 || targetIndex >= newComponents.length) return;

    // Swap positions
    [newComponents[index], newComponents[targetIndex]] = [newComponents[targetIndex], newComponents[index]];
    setTitleComponents(newComponents);
  };

  const handleDeleteComponent = (index) => {
    const newComponents = titleComponents.filter((_, i) => i !== index);
    setTitleComponents(newComponents);
  };

  const handleTransformChange = (index, transformUpdates) => {
    const newComponents = [...titleComponents];
    const component = newComponents[index];
    newComponents[index] = {
      ...component,
      transform: {
        ...component.transform,
        ...transformUpdates,
      },
    };
    setTitleComponents(newComponents);
  };

  const handleTextCaseChange = (index, textCase) => {
    handleTransformChange(index, { transformType: 'TEXT', textCase });
  };

  const handleSpaceReplacementChange = (index, spaceReplacement) => {
    handleTransformChange(index, { transformType: 'TEXT', spaceReplacement });
  };

  const handleDateStyleChange = (index, dateStyle) => {
    handleTransformChange(index, { transformType: 'DATE', dateStyle });
  };

  const handleLiteralEdit = (index, newValue) => {
    const newComponents = [...titleComponents];
    newComponents[index] = { ...newComponents[index], value: newValue };
    setTitleComponents(newComponents);
  };

  const buildUpdate = (previous, addNotification) => {
    const retentionDaysChanged = retentionDays !== previous.retentionDays;
    const emailJoinTypeChanged = emailJoinType !== previous.emailJoinType;
    const shouldIncludeReceivedAtChanged =
      shouldIncludeReceivedAt !== previous.shouldIncludeReceivedAt;
    const shouldSendEmailSuccessAckChanged =
      shouldSendEmailSuccessAck !== previous.shouldSendEmailSuccessAck;
    const defaultDocumentTypeFieldChanged =
      defaultDocumentTypeField !== previous.defaultDocumentTypeField;
    const titleComponentsChanged = JSON.stringify(titleComponents) !== JSON.stringify(previous.titleComponents);

    const documentConfigUpdate = {};

    if (retentionDaysChanged) {
      // Convert days to ISO 8601 duration format
      documentConfigUpdate.maxFiledAwayAge = daysToDuration(retentionDays);
    }

    if (titleComponentsChanged) {
      const validationErrors = validateTitleComponents(titleComponents);
      if (validationErrors.length > 0) {
        addNotification('title-config-validation', validationErrors[0].error, { isError: true });
        return { changed: false, update: documentConfigUpdate, aborted: true };
      }
      documentConfigUpdate.titleComponents = prepareTitleComponentsForSave(titleComponents);
    }

    if (emailJoinTypeChanged) {
      documentConfigUpdate.emailJoinType = emailJoinType;
    }

    if (shouldIncludeReceivedAtChanged) {
      documentConfigUpdate.shouldIncludeReceivedAt = shouldIncludeReceivedAt;
    }

    if (shouldSendEmailSuccessAckChanged) {
      documentConfigUpdate.shouldSendEmailSuccessAck = shouldSendEmailSuccessAck;
    }

    if (defaultDocumentTypeFieldChanged) {
      documentConfigUpdate.defaultDocumentTypeField = defaultDocumentTypeField;
    }

    const changed =
      retentionDaysChanged ||
      emailJoinTypeChanged ||
      shouldIncludeReceivedAtChanged ||
      shouldSendEmailSuccessAckChanged ||
      defaultDocumentTypeFieldChanged ||
      titleComponentsChanged;

    return { changed, update: documentConfigUpdate, aborted: false };
  };

  return {
    retentionDays,
    setRetentionDays,
    emailJoinType,
    setEmailJoinType,
    shouldIncludeReceivedAt,
    setShouldIncludeReceivedAt,
    shouldSendEmailSuccessAck,
    setShouldSendEmailSuccessAck,
    defaultDocumentTypeField,
    setDefaultDocumentTypeField,
    titleComponents,
    editingTitleComponent,
    setEditingTitleComponent,
    hasTitleComponentErrors,
    handleAddToken,
    handleAddLiteral,
    handleMoveComponent,
    handleDeleteComponent,
    handleTextCaseChange,
    handleSpaceReplacementChange,
    handleDateStyleChange,
    handleLiteralEdit,
    applyFetched,
    snapshot,
    restore,
    buildUpdate,
  };
};

export default useConfigDocument;
